import { useContext, useState, ReactNode } from 'react';
import { RouterContext, goTo } from 't-lib-ui/router';
import ThreeColumnLayout, { requestStartCollapsed } from './ThreeColumnLayout';
import LeftSidebar from './LeftSidebar';

interface MainLayoutProps {
  selectedFeed?: string;
  onSelectFeed?: (feedId: string, isReselect?: boolean) => void;
  middle?: ReactNode | ((props: { showExpandButton: boolean; onExpand: () => void; onArticleSelect?: () => void; isMobile: boolean }) => ReactNode);
  right: ReactNode | ((props: { showExpandButton: boolean; onExpand: () => void; isMobile: boolean }) => ReactNode);
  showMiddle?: boolean;
  startWithLeftCollapsed?: boolean;
}

/**
 * Common layout component that wraps ThreeColumnLayout with LeftSidebar.
 * Used by most main app pages (Main, AddFeedPage, AdminFeedsPage, etc.)
 */
const MainLayout: React.FC<MainLayoutProps> = ({
  selectedFeed = 'all-items',
  onSelectFeed,
  middle,
  right,
  showMiddle = false,
  startWithLeftCollapsed = false,
}) => {
  const routerCtx = useContext(RouterContext);
  const [internalSelectedFeed, setInternalSelectedFeed] = useState<string>(selectedFeed);

  const handleSelectFeed = (feedId: string, isReselect?: boolean) => {
    if (onSelectFeed) {
      onSelectFeed(feedId, isReselect);
    } else {
      // Cross-page navigation to a feed (e.g. sidebar click from HomePage): open the
      // destination straight into read mode on narrow/mobile screens.
      if (feedId != 'discover') requestStartCollapsed();
      setInternalSelectedFeed(feedId);
      if (feedId == 'all-items')
        goTo(routerCtx, '/a/new');
      else if (feedId == 'starred')
        goTo(routerCtx, '/a/starred');
      else if (feedId == 'read-later')
        goTo(routerCtx, '/a/read-later');
      else if (feedId == 'discover')
        goTo(routerCtx, '/a/discover');
      else
        goTo(routerCtx, `/a/feed/${feedId}`);
    }
  };

  const currentSelectedFeed = onSelectFeed ? selectedFeed : internalSelectedFeed;

  return (
    <ThreeColumnLayout
      left={({ onCollapse, onMenuClick, isMobile, isDrawer }) => (
        <LeftSidebar
          selectedFeed={currentSelectedFeed}
          onSelectFeed={handleSelectFeed}
          onCollapse={onCollapse}
          onMenuClick={onMenuClick}
          isMobile={isMobile}
          isDrawer={isDrawer}
        />
      )}
      middle={middle}
      right={right}
      showMiddle={showMiddle}
      showLeft={true}
      startWithLeftCollapsed={startWithLeftCollapsed}
    />
  );
};

export default MainLayout;
